/**
 * Letters reveal — a small, reusable one-shot text animation.
 *
 * Splits an element's text into per-letter spans and, the first time the element
 * scrolls into view, settles each letter into place with a staggered rise + fade
 * (moving-letters style, à la tobiasahlin.com/moving-letters #6). Both the split
 * and the hidden pre-animation state are applied by JS only, so with no JS or a
 * reduced-motion preference the original text stays fully visible and untouched.
 *
 * The visual (rise, duration, easing, per-letter stagger) lives in SCSS via the
 * `letters-reveal` mixin, keyed off the class contract used here: `c-letters`
 * marks the root, `c-letters__letter` is each letter's clipping mask (carrying a
 * `--letter-index`) wrapping a `c-letters__glyph` that actually moves,
 * `is-letters-armed` is the hidden pre-animation state and `is-letters-in` runs
 * the staggered reveal. The two-span split mirrors moving-letters #6: the outer
 * span masks (overflow) while the inner glyph slides up from below it, so each
 * letter unfolds from its own baseline instead of merely fading in.
 *
 * Two opt-in extras: `wrapWords` groups letters into `c-letters__word` spans so
 * multi-line text never wraps mid-word, and `loop` replays the reveal forever
 * (reveal, then `is-letters-out`, then re-arm). Looping relies on the mixin
 * styling `is-letters-out` with a transition on the root's opacity (see the
 * `letters-reveal-pop` mixin), since that transition's end drives the restart.
 */

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Wrap each character of the element's text in a letter span, preserving hard
 * line breaks (<br>) and inter-word spacing, and set an accessible label so
 * assistive tech announces the whole phrase instead of spelling it out.
 *
 * @param {HTMLElement} el        The element whose text should be split.
 * @param {boolean}     wrapWords Group each word's letters in a `c-letters__word` span.
 * @return {void}
 */
const splitLetters = ( el, wrapWords ) => {

	const fragment = document.createDocumentFragment();
	let label = '';
	let index = 0;
	let word = null;

	Array.from( el.childNodes ).forEach( ( node ) => {

		// Keep hard line breaks as-is; they also read as a space in the label.
		if ( Node.ELEMENT_NODE === node.nodeType && node.nodeName === 'BR' ) {

			word = null;
			fragment.appendChild( node.cloneNode() );
			label += ' ';
			return;

		}

		// Anything that is not a text node (unexpected inline markup) is kept
		// verbatim so we never silently drop content we did not plan for.
		if ( Node.TEXT_NODE !== node.nodeType ) {

			word = null;
			fragment.appendChild( node.cloneNode( true ) );
			label += node.textContent;
			return;

		}

		Array.from( node.textContent ).forEach( ( char ) => {

			label += char;

			// Whitespace keeps the words apart but is not itself animated, so it
			// stays a plain text node between the inline-block letters.
			if ( char.trim() === '' ) {

				word = null;
				fragment.appendChild( document.createTextNode( char ) );
				return;

			}

			// A word span stays open until the next whitespace, so its letters
			// share one non-breaking box.
			if ( wrapWords && ! word ) {

				word = document.createElement( 'span' );
				word.className = 'c-letters__word';
				fragment.appendChild( word );

			}

			// Outer span = per-letter clipping mask; inner glyph = the part that
			// actually slides up from below it (moving-letters #6 unfold).
			const letter = document.createElement( 'span' );
			letter.className = 'c-letters__letter';
			letter.style.setProperty( '--letter-index', String( index ) );

			const glyph = document.createElement( 'span' );
			glyph.className = 'c-letters__glyph';
			glyph.textContent = char;

			letter.appendChild( glyph );
			( word || fragment ).appendChild( letter );
			index += 1;

		} );

	} );

	el.setAttribute( 'aria-label', label.replace( /\s+/g, ' ' ).trim() );
	el.replaceChildren( fragment );

};

/**
 * Turn an element into a one-shot, scroll-triggered letters reveal.
 *
 * Bails (leaving the original text visible and unmodified) when the element is
 * missing, when reduced motion is preferred, or when IntersectionObserver is
 * unavailable — so the effect is purely additive.
 *
 * @param {HTMLElement} el                  The element to animate.
 * @param {Object}      [options]           Behaviour overrides.
 * @param {number}      [options.threshold] Visibility ratio that triggers the reveal.
 * @param {boolean}     [options.wrapWords] Keep each word's letters in one non-breaking span.
 * @param {boolean}     [options.loop]      Replay the reveal (with an `is-letters-out` phase) forever.
 * @return {void}
 */
export const initLettersReveal = ( el, options = {} ) => {

	if ( ! el ) {

		return;

	}

	if ( prefersReducedMotion() || ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	const { threshold = 0.25, wrapWords = false, loop = false } = options;

	splitLetters( el, wrapWords );

	// Arm the hidden pre-animation state.
	el.classList.add( 'c-letters', 'is-letters-armed' );

	/**
	 * Chain the loop off transition ends: the last glyph settling starts the
	 * out phase, the root's opacity fade finishing re-arms and replays the reveal.
	 * Removing the state classes snaps back (no transition on the armed state),
	 * and the replay waits two frames so that snap paints first.
	 *
	 * @return {void}
	 */
	const runLoop = () => {

		const glyphs = el.querySelectorAll( '.c-letters__glyph' );
		const lastGlyph = glyphs[ glyphs.length - 1 ];

		el.addEventListener( 'transitionend', ( event ) => {

			if ( event.target === lastGlyph && event.propertyName === 'transform' ) {

				el.classList.add( 'is-letters-out' );

			} else if ( event.target === el && event.propertyName === 'opacity' ) {

				el.classList.remove( 'is-letters-in', 'is-letters-out' );

				requestAnimationFrame( () => requestAnimationFrame( () => el.classList.add( 'is-letters-in' ) ) );

			}

		} );

	};

	// Reveal once the element scrolls into view. The observer is wired up only
	// after the armed (hidden) state has had a frame to paint, so an element
	// already in the viewport on load still transitions in rather than snapping.
	/**
	 * Observe the element and trigger the reveal the first time it is visible.
	 *
	 * @return {void}
	 */
	const reveal = () => {

		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						el.classList.add( 'is-letters-in' );
						observer.disconnect();

						if ( loop ) {

							runLoop();

						}

					}

				} );

			},
			{ threshold },
		);

		observer.observe( el );

	};

	/**
	 * Arm the IntersectionObserver a couple of frames out, so the freshly-added
	 * armed (hidden) state has had a chance to paint first.
	 *
	 * @return {void}
	 */
	const armObserver = () => requestAnimationFrame( () => requestAnimationFrame( reveal ) );

	// Wait for the custom font already requested by `el`'s own styles to finish
	// loading before wiring up the trigger. Without this, a block already in the
	// viewport on load reveals letter-by-letter in the fallback font, then jumps
	// as each glyph reflows to its final size once the real font swaps in —
	// reading as broken rather than as a deliberate entrance.
	if ( document.fonts && document.fonts.ready ) {

		document.fonts.ready.then( armObserver );

	} else {

		armObserver();

	}

};
